/**
 * Estilos compartilhados entre TableV2 (DataGrid) e ExpandableTableV2 (TanStack + MUI Table)
 * para manter paridade visual entre telas V2.
 */

/** Tipografia das células — alinhada ao padrão MUI DataGrid */
export const TABLE_V2_CELL_FONT_SIZE = '0.875rem';
export const TABLE_V2_CELL_LINE_HEIGHT = '1.43';
export const TABLE_V2_ROW_HEIGHT_DEFAULT = 52;
/** Altura do column header no DataGrid v8 (linhas de dados usam rowHeight) */
export const TABLE_V2_HEADER_HEIGHT_DEFAULT = 56;

export const tableV2ContainerSx = {
  backgroundColor: 'background.paper',
  borderRadius: 3,
  boxShadow: (theme) => (theme.palette.mode === 'dark' ? 3 : 1),
};

export const tableV2HeaderActionsBoxSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  p: 2,
};

export const tableV2HeaderButtonSx = (index) => (index > 0 ? { ml: 1 } : undefined);

/** Área da grade com altura fixa — uso no TableV2 (DataGrid preenche o espaço) */
export const tableV2AreaSx = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: {
    xs: 'calc(100vh - 320px)',
    md: 'calc(100vh - 260px)',
  },
  minHeight: 420,
  borderRadius: 2,
  overflow: 'hidden',
};

const TABLE_V2_AREA_MAX_HEIGHT = {
  xs: 'calc(100vh - 320px)',
  md: 'calc(100vh - 260px)',
};

/**
 * Área da grade com altura pelo conteúdo (até maxHeight) — ExpandableTableV2.
 * Evita faixa em branco abaixo das linhas quando há poucos registros.
 */
export const tableV2AreaContentFitSx = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: TABLE_V2_AREA_MAX_HEIGHT,
  borderRadius: 2,
  overflow: 'hidden',
};

export const tableV2LoadingBodyFitSx = (loading) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '0 1 auto',
  minHeight: 0,
  maxHeight: TABLE_V2_AREA_MAX_HEIGHT,
  opacity: loading ? 0.38 : 1,
  transition: (theme) =>
    theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
    }),
  pointerEvents: loading ? 'none' : 'auto',
});

/** Container rolável da tabela MUI (altura = conteúdo, limitado pelo max da área) */
export const tableV2TableContainerFitSx = {
  flex: '0 1 auto',
  overflow: 'auto',
  maxHeight: {
    xs: 'calc(100vh - 320px - 52px)',
    md: 'calc(100vh - 260px - 52px)',
  },
};

export const tableV2LoadingLinearSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 4,
  height: 3,
};

export const tableV2LoadingBodySx = (loading) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minHeight: 0,
  opacity: loading ? 0.38 : 1,
  transition: (theme) =>
    theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
    }),
  pointerEvents: loading ? 'none' : 'auto',
});

/** Espelha TableV2: sem outline de foco nas células */
export const tableV2CellFocusSx = {
  '&:focus, &:focus-within': {
    outline: 'none',
  },
};

export const tableV2TableSx = {
  border: 'none',
  width: '100%',
  '& .MuiTableCell-root': {
    borderColor: 'divider',
    boxSizing: 'border-box',
    ...tableV2CellFocusSx,
  },
};

export const getTableV2HeaderCellSx = (
  headerHeight = TABLE_V2_HEADER_HEIGHT_DEFAULT
) => ({
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark'
      ? theme.palette.neutral[900]
      : theme.palette.neutral[50],
  fontWeight: 600,
  fontSize: TABLE_V2_CELL_FONT_SIZE,
  lineHeight: TABLE_V2_CELL_LINE_HEIGHT,
  color: 'text.primary',
  height: headerHeight,
  minHeight: headerHeight,
  maxHeight: headerHeight,
  py: 0,
  px: '10px',
  borderBottom: '1px solid',
  borderColor: 'divider',
  ...tableV2CellFocusSx,
});

export const getTableV2BodyCellSx = (rowHeight = TABLE_V2_ROW_HEIGHT_DEFAULT) => ({
  fontSize: TABLE_V2_CELL_FONT_SIZE,
  lineHeight: TABLE_V2_CELL_LINE_HEIGHT,
  fontWeight: 400,
  color: 'text.primary',
  height: rowHeight,
  minHeight: rowHeight,
  maxHeight: rowHeight,
  py: 0,
  px: '10px',
  borderBottom: '1px solid',
  borderColor: 'divider',
  verticalAlign: 'middle',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  ...tableV2CellFocusSx,
});

/** Zebra sem hover manual — usar com `<TableRow hover />` */
export const getTableV2MasterRowSx = (index) => ({
  backgroundColor: (theme) =>
    index % 2 === 0
      ? theme.palette.mode === 'dark'
        ? theme.palette.neutral[900]
        : theme.palette.neutral[50]
      : theme.palette.mode === 'dark'
        ? theme.palette.neutral[800]
        : theme.palette.neutral[100],
});

/** @deprecated Use getTableV2MasterRowSx + TableRow hover */
export const getTableV2RowZebraSx = (index) => ({
  ...getTableV2MasterRowSx(index),
  '&:hover': {
    backgroundColor: (theme) => theme.palette.action.hover,
  },
});

/** Conteúdo textual dentro de células custom (renderCell / TanStack cell) */
export const tableV2CellContentSx = {
  fontSize: TABLE_V2_CELL_FONT_SIZE,
  lineHeight: TABLE_V2_CELL_LINE_HEIGHT,
  color: 'text.primary',
};

export const tableV2CellCaptionSx = {
  mt: 0.25,
  fontSize: '0.75rem',
  lineHeight: 1.33,
  color: 'primary.main',
};

/** Chips de contagem (locais, tamanhos) — altura compatível com rowHeight 52 */
export const tableV2ChipSx = {
  height: 24,
  maxHeight: 24,
  fontWeight: 500,
  color: 'text.primary',
  borderColor: 'divider',
  '& .MuiChip-label': {
    px: 1,
    fontSize: '0.8125rem',
    lineHeight: 1.25,
  },
};

export const tableV2PaginationSx = {
  flexShrink: 0,
  borderTop: '1px solid',
  borderColor: 'divider',
  color: 'text.primary',
  '& .MuiTablePagination-toolbar': {
    minHeight: 52,
    px: 1,
  },
  '& .MuiTablePagination-selectLabel': {
    fontSize: TABLE_V2_CELL_FONT_SIZE,
    color: 'text.secondary',
    mb: 0,
    mt: 0,
  },
  '& .MuiTablePagination-displayedRows': {
    fontSize: TABLE_V2_CELL_FONT_SIZE,
    color: 'text.secondary',
    mb: 0,
    mt: 0,
  },
  '& .MuiTablePagination-select': {
    fontSize: TABLE_V2_CELL_FONT_SIZE,
  },
  '& .MuiTablePagination-actions': {
    color: 'text.secondary',
  },
  '& .MuiIconButton-root': {
    color: 'text.secondary',
  },
};

export const tableV2EmptyStateSx = {
  py: 6,
  textAlign: 'center',
  fontSize: TABLE_V2_CELL_FONT_SIZE,
  lineHeight: TABLE_V2_CELL_LINE_HEIGHT,
  color: 'text.secondary',
};

export const tableV2SkeletonCellSx = (rowHeight = TABLE_V2_ROW_HEIGHT_DEFAULT) => ({
  height: rowHeight,
  minHeight: rowHeight,
  py: 0,
  px: '10px',
  borderBottom: '1px solid',
  borderColor: 'divider',
});

/** Linha master quando o painel de detalhe está aberto */
export const getTableV2ExpandedMasterRowSx = (baseZebraSx) => ({
  ...baseZebraSx,
  borderLeft: '3px solid',
  borderLeftColor: 'primary.main',
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark'
      ? `${theme.palette.primary.main}14`
      : `${theme.palette.primary.main}0D`,
});

/** Container do painel de detalhe (sub-tabela expansível) */
export const tableV2DetailPanelWrapperSx = {
  mx: { xs: 1, sm: 2 },
  my: 1,
  pb: 1,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  borderLeftWidth: 3,
  borderLeftColor: 'primary.main',
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark'
      ? theme.palette.neutral[800]
      : theme.palette.neutral[100],
  boxShadow: (theme) => (theme.palette.mode === 'dark' ? 0 : 1),
  overflow: 'hidden',
};

export const tableV2DetailPanelTitleSx = {
  px: 2,
  pt: 1.5,
  pb: 0.5,
  fontSize: '0.75rem',
  fontWeight: 600,
  lineHeight: 1.33,
  color: 'text.secondary',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

/** Célula da sub-tabela dentro do painel — fundo paper para contraste com o container */
export const getTableV2DetailBodyCellSx = (
  rowHeight,
  rowIndex = 0
) => ({
  ...getTableV2BodyCellSx(rowHeight),
  backgroundColor: (theme) =>
    rowIndex % 2 === 0
      ? theme.palette.background.paper
      : theme.palette.mode === 'dark'
        ? theme.palette.neutral[900]
        : theme.palette.neutral[50],
});

/** Linha do TableRow que envolve o colspan do painel de detalhe */
export const tableV2DetailPanelRowSx = {
  p: 0,
  py: 0.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'transparent',
};
